import { Paper } from "@mui/material";
import Typography from "@mui/material/Typography";
import { DateWithTooltip } from "components/DateWithTooltip";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import PropTypes from "prop-types";
import React, { useMemo, useContext } from "react";
import { styled } from "@mui/material/styles";
import { AlertTimeSeriesChart } from "components/AlertsManagement/Chart";
import orderBy from "lodash/orderBy";
import { AlertInfoActions } from "components/AlertsManagement/AlertInfo/AlertInfoActions";
import { AlertOccurrenceList } from "components/AlertsManagement/AlertInfo/AlertOccurrenceList";
import { ScrollablePanel } from "components/ScrollablePanel";
import { LocationOn } from "@mui/icons-material";
import { SeverityContainer } from "components/SeverityContainer";
import { LinkButton } from "components/LinkButton";
import { FeaturesContext } from "contexts/FeaturesContext";
import { EntitlementContext } from "contexts/UserProfileContext";

export function AlertInfo({
  alert,
  alertsLoading,
  alertsIsEmpty,
  onCreateCaseComplete,
  onClickDismissAlert
}) {
  return (
    <FixedPaper>
      <ProgressIndicatorContainer loading={alertsLoading && !alert}>
        {alert ? (
          <ScrollablePanel
            header={
              <HeaderWrapper>
                <TitleWrapper>
                  <Typography variant="h4" noWrap title={alert.rule_name}>
                    {alert.rule_name}
                  </Typography>
                  <Typography variant="subtitle1" noWrap>
                    <DateWithTooltip date={alert.alert_date} showTime />
                  </Typography>
                </TitleWrapper>
                <AlertInfoActions
                  alert={alert}
                  onCreateCaseComplete={onCreateCaseComplete}
                  onClickDismissAlert={onClickDismissAlert}
                />
              </HeaderWrapper>
            }
            footer={<ExploreButton alert={alert} />}
          >
            <ContentWrapper>
              <Section>
                <SeverityContainer severityId={alert.severity_type_id} />
                <Typography variant="h5">
                  Turbine: {alert.machine_name}
                </Typography>
                <InfoWrapper>
                  <LocationOn />
                  <Typography>{alert.site_name}</Typography>
                </InfoWrapper>
                <Typography>Component: {alert.component_name}</Typography>
              </Section>
              <Section>
                <Typography variant="h5">Alert Chart</Typography>
                <ChartWrapper>
                  <AlertTimeSeriesChart
                    tagId={alert.tag_id}
                    tagName={alert.tag_name}
                    tagUnit={alert.tag_unit}
                    machineId={alert.machine_id}
                    severityTypeId={alert.severity_type_id}
                    ruleId={alert.rule_id}
                    ruleName={alert.rule_name}
                  />
                </ChartWrapper>
              </Section>
              <Section>
                <Typography variant="h5">Notes / Recommendations</Typography>
                <Typography variant="body1">
                  {alert.recommendation || "No notes/recommendations"}
                </Typography>
              </Section>
              <Section>
                <Typography variant="h5">Alert Period History</Typography>
                <AlertOccurrenceList
                  alertOccurrences={orderBy(
                    alert.alert_occurrences.filter(
                      x => x.id !== alert.latest_occurrence_id
                    ),
                    "alert_date",
                    "desc"
                  )}
                />
              </Section>
            </ContentWrapper>
          </ScrollablePanel>
        ) : (
          <NoAlertWrapper>
            <Typography variant="h5" color="primary">
              {alertsIsEmpty
                ? "There are no alerts to show"
                : "There is no alert selected"}
            </Typography>
          </NoAlertWrapper>
        )}
      </ProgressIndicatorContainer>
    </FixedPaper>
  );
}

AlertInfo.propTypes = {
  alert: PropTypes.object,
  alertsLoading: PropTypes.bool.isRequired,
  alertsIsEmpty: PropTypes.bool.isRequired,
  onCreateCaseComplete: PropTypes.func.isRequired,
  onClickDismissAlert: PropTypes.func.isRequired
};

const ExploreButton = ({ alert }) => {
  const { hasFeature } = useContext(FeaturesContext);
  const { hasEntitlement } = useContext(EntitlementContext);

  const dataExplorationV2Enabled = useMemo(
    () =>
      hasFeature("data_exploration_v2") &&
      hasEntitlement("data-exploration-v2-view"),
    [hasEntitlement, hasFeature]
  );

  if (dataExplorationV2Enabled) {
    return (
      <div>
        <LinkButton
          color="primary"
          to={`/pages/data-exploration?alert=${alert.alert_id}`}
          variant="outlined"
        >
          Explore
        </LinkButton>
      </div>
    );
  } else {
    return null;
  }
};

ExploreButton.propTypes = {
  alert: PropTypes.object
};

const FixedPaper = styled(Paper)`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  max-height: calc(100vh - 7rem);
  height: calc(100vh - 7rem);
  gap: ${props => props.theme.spacing(2)};
`;

const HeaderWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: ${props => props.theme.spacing(2)};
`;

const TitleWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
  flex-grow: 1;
`;

const ChartWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 3px;
`;

const NoAlertWrapper = styled("div")`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const InfoWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: ${props => props.theme.spacing(1)};
`;
